import { trigger, state, style, transition, animate } from '@angular/animations';
import { formatNumber } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import moment from 'moment';
import { AppService, ValueIdPair } from 'src/app/_services/app.service';

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css'],
    animations: [
        trigger('slider', [
            state('*', style({
                height: 0
            })),
            state('show', style({
                height: '*'
            })),
            transition('* <=> show', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
        ])
    ],
    standalone: false
})
export class SearchComponent implements OnInit, AfterViewInit {

  markets: ValueIdPair[] = [];
  isGeneratedSearch: boolean = false;
  search: Search;

  dataSource;
  displayColumns: string[] = ['Description', 'Item', 'Value', 'Date'];
  recordsLength: number = 0;
  isLoading: boolean = false;
  mattablePageSize: number = 10;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(@Inject('BASE_URL') private baseUrl: string, private http: HttpClient, private service: AppService) {
    this.service.SetTranslation('tools/search'); }

  ngOnInit() {
    this.InitializeForm();
    this.InitializeFilters();
    this.GetMarkets();
  }

  ngAfterViewInit() {

   // // If the user changes the sort order, reset back to the first page.
   this.sort.sortChange.subscribe(() => {
     this.paginator.pageIndex = 0;
     this.ApplyFilterOptions(false);
   });

   this.paginator.page.subscribe(() => {
     this.ApplyFilterOptions(false);
   });
  }

  /** List all the markets */
  GetMarkets() {
    this.http.get<ValueIdPair[]>(this.baseUrl + 'api/reports/getMarkets').subscribe(result => {
      this.markets = result;
    }, error => {
      console.error(error);
    });
  }

  /** Initializes the search form */
  InitializeForm() {
    this.search = {
      marketId: '',
      dateFrom: new Date(),
      dateTo: new Date(),
      descriptionFilter: null,
      itemFilter: null,
      valueFilter: null,
      dateFilter: null
    };
  }

  /** Initializes the search table header filters */
  InitializeFilters() {
    this.search.descriptionFilter = {
      filterValue: '',
      filterEnabled: false,
      filterType: 'temp'
    };
    this.search.itemFilter = {
      filterValue: '',
      filterEnabled: false,
      filterType: 'temp'
    };
    this.search.valueFilter = {
      filterValue: '',
      filterEnabled: false,
      filterType: 'temp'
    };
    this.search.dateFilter = {
      filterValue: '',
      filterEnabled: false,
      filterType: ''
    };
  }

  /** Change event for the page size input */
  OnPagesizeChange() {
    this.paginator.pageSize = this.mattablePageSize;
    this.paginator.pageIndex = 0;
    this.ApplyFilterOptions(false);
  }

  /** Click event for the search button */
  OnClickSearch() {
    if (this.search.marketId && (this.search.dateTo || this.search.dateTo)) {
      if (this.search.dateTo || this.search.dateTo) {
        this.GetSearchRecords();
      } else {
        this.service.SaveStatus(false, 'Search.header', 'Search.Messages.msgErrorDateRange');
      }
    }
  }

  /** Export to excel */
  OnClickExport() {
    this.service.showSpinner();
    this.http.post(this.baseUrl + 'api/reports/exportSearch/' + this.sort.active + '/' + this.sort.direction, this.search, { responseType: 'text' }).subscribe(result => {
      if (result !== '') {
        const byteCharacters = atob(result);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        let marketCode = this.markets.find(o => o.id === this.search.marketId).value;
        marketCode = marketCode.slice(marketCode.indexOf('(') + 1, marketCode.indexOf(')'));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.download = 'Search Data - ' + marketCode + ' (' + moment(this.search.dateFrom).format('YYYY-MM-DD') + ' - ' + moment(this.search.dateTo).format('YYYY-MM-DD') + ').xlsx';
        anchor.href = url;
        anchor.click();
        document.removeChild(anchor);
      } else {
        this.service.SaveStatus(false, 'Messages.msgHeaderExport', 'Messages.msgErrorTryAgain');
      }
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
    });
  }

  /** Teset the date filter */
  ResetDateFilter() {
    if (this.search.dateFilter.filterValue !== '') {
      this.search.dateFilter.filterValue = '';
      this.ApplyFilterOptions(false);
    }
  }

  /** Apply the column filter options */
  ApplyFilterOptions(opened: boolean) {
    if (!opened) {
      this.GetSearchRecords();
    }
  }

  /** Gets the search result */
  GetSearchRecords() {
    if (this.dataSource) {
      this.dataSource = [];
    }
    this.isLoading = true;
    this.service.showSpinner();
    this.http.post<SearchData>(this.baseUrl + 'api/reports/searchData/' + this.sort.active + '/' + this.sort.direction + '/' + this.paginator.pageIndex + '/' + this.paginator.pageSize, this.search).subscribe(result => {
      this.isLoading = false;

      if (result.records.length > 0) {
        result.records.forEach(ele => {
          if (!isNaN(Number(ele.value)) && ele.value !== '') {
            let temp: string = ele.value;
            if (temp.includes('.')) {
              temp = temp.replace('-', '');
              const beforeDec = temp.split('.')[0].length.toString();
              const afterDec = temp.split('.')[1].length.toString();
              ele.value = formatNumber(Number(ele.value), this.service.getLocalLanguage(), beforeDec + '.' + afterDec + '-' + afterDec);
            }
          }
        });
      }

      this.dataSource = result.records;
      this.recordsLength = result.totalCount;
      this.PopulateFilters();
      this.isGeneratedSearch = true;
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
    });
  }

  /** Populates the filters with updated filter enabled property */
  PopulateFilters() {
    if (this.search.descriptionFilter.filterType !== 'temp' && this.search.descriptionFilter.filterType !== 'noFilter' && this.search.descriptionFilter.filterValue !== '') {
      this.search.descriptionFilter.filterEnabled = true;
    } else {
      this.search.descriptionFilter.filterEnabled = false;
    }

    if (this.search.itemFilter.filterType !== 'temp' && this.search.itemFilter.filterType !== 'noFilter' && this.search.itemFilter.filterValue !== '') {
      this.search.itemFilter.filterEnabled = true;
    } else {
      this.search.itemFilter.filterEnabled = false;
    }

    if (this.search.valueFilter.filterType !== 'temp' && this.search.valueFilter.filterType !== 'noFilter' && this.search.valueFilter.filterValue !== '') {
      this.search.valueFilter.filterEnabled = true;
    } else {
      this.search.valueFilter.filterEnabled = false;
    }

    if (this.search.dateFilter.filterValue !== '') {
      this.search.dateFilter.filterEnabled = true;
    } else {
      this.search.dateFilter.filterEnabled = false;
    }
  }


}

/** An interface to access search parameters */
interface Search {
  marketId: string;
  dateFrom: Date;
  dateTo: Date;
  descriptionFilter: SearchColumnFilter;
  itemFilter: SearchColumnFilter;
  valueFilter: SearchColumnFilter;
  dateFilter: SearchColumnFilter;
}

/** An interface to map server response for the search results  */
interface SearchData {
  records: SearchRecord[];
  totalCount: number;
}

/** An interface to access a record of the search results */
interface SearchRecord {
  description: string;
  item: string;
  value: string;
  date: string;
}

/** An interface to access column filter details */
interface SearchColumnFilter {
  filterValue: string;
  filterType: string;
  filterEnabled: boolean;
}
